import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { getOrdersByEmail, getUserOrders } from '../services/orderService';
import { validateEmail } from '../utils/security';

const formatDate = (value) => {
  try {
    if (!value) return '-';
    if (typeof value?.toDate === 'function') {
      return value.toDate().toLocaleString();
    }
    return new Date(value).toLocaleString();
  } catch {
    return String(value || '-');
  }
};

const MyOrders = () => {
  const { currency, exchangeRates } = useCurrency();
  const { currentUser, userDoc } = useAuth();

  const [email, setEmail] = useState(() => {
    return '';
  });
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const next =
      typeof userDoc?.profile?.email === 'string'
        ? userDoc.profile.email
        : typeof userDoc?.email === 'string'
          ? userDoc.email
          : '';
    if (next && !email) setEmail(next);
  }, [userDoc?.profile?.email, userDoc?.email]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const normalizedEmail = String(email || '').trim();
      if (validateEmail(normalizedEmail)) {
        const res = await getOrdersByEmail(normalizedEmail);
        setOrders(Array.isArray(res) ? res : []);
        return;
      }

      if (currentUser?.uid) {
        const res = await getUserOrders(currentUser.uid);
        setOrders(Array.isArray(res) ? res : []);
        return;
      }

      setOrders([]);
    } catch {
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid, email]);

  useEffect(() => {
    load();
  }, [load]);

  const formatOrderAmount = (order, field = 'total') => {
    const code = String(order?.currency || currency || '').trim();
    const symbol = exchangeRates?.[code]?.symbol;

    const pricing = order?.pricing;
    const base = Number(order?.[field] || 0);

    let display = pricing?.[`${field}Display`];
    if (display === undefined && field === 'total') display = order?.totalDisplay;
    const n = Number(display === undefined ? base : display);

    const formatted = code === 'JPY' ? String(Math.round(n)) : n.toFixed(2);
    if (symbol) return `${symbol}${formatted}`;
    if (code) return `${code} ${formatted}`;
    return formatted;
  };

  const sortedOrders = useMemo(() => {
    const getMillis = (v) => {
      if (!v) return 0;
      if (typeof v?.toMillis === 'function') return v.toMillis();
      if (typeof v?.toDate === 'function') return v.toDate().getTime();
      const n = Date.parse(v);
      if (Number.isFinite(n)) return n;
      const asNum = Number(v);
      return Number.isFinite(asNum) ? asNum : 0;
    };

    return (orders || [])
      .slice()
      .sort((a, b) => getMillis(b.createdAt) - getMillis(a.createdAt));
  }, [orders]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <h1 className="text-2xl font-medium tracking-wide">MY ORDERS</h1>
          <p className="mt-2 text-sm text-gray-600">View your order history (local now, Firebase later).</p>
        </div>
        <Link to="/shop/new-arrivals" className="text-sm underline hover:opacity-60">
          Continue shopping
        </Link>
      </div>

      {currentUser?.isAnonymous ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-[0_10px_30px_rgba(0,0,0,0.06)] mb-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-8">
              <label className="block text-xs tracking-widest text-gray-600">EMAIL</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter the email used at checkout"
                className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
              />
            </div>
            <div className="md:col-span-4">
              <button
                type="button"
                onClick={load}
                className="w-full border border-black py-3 text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
              >
                FIND ORDERS
              </button>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            If you checked out as a guest, enter the same email to find your orders.
          </div>
        </div>
      ) : null}

      <div className="bg-white border border-gray-200 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs tracking-widest text-gray-600">
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-600">
                    Loading orders...
                  </td>
                </tr>
              ) : sortedOrders.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-600">
                    No orders found.
                  </td>
                </tr>
              ) : (
                sortedOrders.map((o) => (
                  <tr key={o.id} className="border-b border-gray-200 last:border-b-0">
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm font-medium text-black">{o.orderNumber || o.id}</div>
                      <div className="mt-1 text-xs text-gray-600">{(o.items || []).length} item(s)</div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border border-gray-200 bg-gray-50 text-gray-700">
                        {String(o.status || 'pending').toUpperCase()}
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm text-gray-800">{formatDate(o.createdAt)}</div>
                    </td>
                    <td className="px-4 py-4 align-top text-right">
                      <div className="text-sm text-gray-800">{formatOrderAmount(o, 'total')}</div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setSelected(o)}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors"
                        >
                          <Eye size={14} />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-6 py-10">
          <button type="button" className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} aria-label="Close" />
          <div className="relative w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-[0_20px_60px_rgba(0,0,0,0.18)] p-6">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-sm font-medium tracking-wide">ORDER DETAILS</div>
                <div className="mt-2 text-sm text-gray-700">{selected.orderNumber || selected.id}</div>
                <div className="mt-1 text-xs text-gray-600">Created: {formatDate(selected.createdAt)}</div>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs tracking-widest text-gray-600">STATUS</div>
                <div className="mt-2 text-sm text-gray-800">{String(selected.status || 'pending').toUpperCase()}</div>
              </div>
              <div>
                <div className="text-xs tracking-widest text-gray-600">TOTAL</div>
                <div className="mt-2 text-sm text-gray-800">{formatOrderAmount(selected, 'total')}</div>
              </div>
            </div>

            <div className="mt-8">
              <div className="text-xs tracking-widest text-gray-600">ITEMS</div>
              <div className="mt-3 space-y-2">
                {(selected.items || []).map((item, idx) => (
                  <div key={`${item.id}-${idx}`} className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start gap-4">
                      {(() => {
                        const src =
                          (Array.isArray(item?.images) && item.images[0]) ||
                          item?.image ||
                          item?.imageUrl ||
                          '';
                        return src ? (
                          <img
                            src={src}
                            alt={item.name || item.id || 'Product'}
                            className="w-14 h-14 rounded-lg border border-gray-200 object-cover bg-gray-50"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg border border-gray-200 bg-gray-50" />
                        );
                      })()}
                      <div>
                        <div className="text-sm font-medium text-black">{item.name || item.id}</div>
                        <div className="mt-1 text-xs text-gray-600">Size: {item.size || '-'} | Color: {item.color || '-'}</div>
                        <div className="mt-1 text-xs text-gray-600">Qty: {item.quantity || 1}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-800">
                      {(() => {
                        const code = String(selected.currency || currency || '').trim();
                        const qty = Number(item.quantity || 1);
                        const prices = item?.prices && typeof item.prices === 'object' ? item.prices : null;
                        const raw = prices && code && Object.prototype.hasOwnProperty.call(prices, code) ? prices[code] : undefined;
                        const unit = Number(raw);
                        const legacy = Number(item.price);
                        const value = Number.isFinite(unit) ? unit * qty : Number.isFinite(legacy) ? legacy * qty : 0;
                        const formatted = code === 'JPY' ? String(Math.round(value)) : value.toFixed(2);
                        return code ? `${code} ${formatted}` : formatted;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {selected.shippingAddress ? (
              <div className="mt-8">
                <div className="text-xs tracking-widest text-gray-600">SHIPPING</div>
                <div className="mt-3 rounded-xl border border-gray-200 p-4 text-sm text-gray-800">
                  <div>{selected.shippingAddress?.firstName || ''} {selected.shippingAddress?.lastName || ''}</div>
                  <div className="mt-1">{selected.shippingAddress?.address || ''}</div>
                  {selected.shippingAddress?.apartment ? <div className="mt-1">{selected.shippingAddress.apartment}</div> : null}
                  <div className="mt-1">{selected.shippingAddress?.city || ''}, {selected.shippingAddress?.state || ''} {selected.shippingAddress?.zipCode || ''}</div>
                  <div className="mt-1">{selected.shippingAddress?.country || ''}</div>
                  <div className="mt-2">Phone: {selected.shippingAddress?.phone || '-'}</div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {currentUser?.isAnonymous ? (
        <div className="mt-6 text-center">
          <Link to="/contact" className="text-sm underline hover:opacity-60">
            Need help with an order?
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default MyOrders;
